import type {
  Content,
  CustomTableLayout,
  TableCell,
  TDocumentDefinitions,
} from 'pdfmake/interfaces.js';
import { formatDay } from '../../format.js';
import { TOKENS } from '../../tokens.js';
import { revisionText, vesselOffsetMinutes } from '../documents/library.js';
import { signatureHeading, signatureRows } from './record-model.js';
import type { FormRecordSource } from './source.js';
import { fileSizeText, recordTime, shortHash, utcOffsetLabel } from './status.js';
import type { FormRecordDetail, FormTemplate } from './types.js';

/** Çevirici: i18next `t` ya da worker'ın sözlüğü. */
export type RecordT = (key: string, options?: Record<string, unknown>) => string;

type PrintSource = Pick<FormRecordSource, 'printer' | 'software'>;

/**
 * Print Form'un bastığı dosya (ADR 0042 §20; ADR 0028 #34(3)): revizyonun onaylı baskı PDF'i
 * varsa o, yoksa ana dosya. Kaydın `template_role`'ü ve `template_sha256`'sı sunucuda bununla
 * yazılır (Ek U14).
 */
export function printedTemplate(template: Pick<FormTemplate, 'printRendition' | 'source'>): {
  role: 'source' | 'print_rendition';
  sha256: string;
} {
  return template.printRendition
    ? { role: 'print_rendition', sha256: template.printRendition.sha256 }
    : { role: 'source', sha256: template.source.sha256 };
}

/**
 * Kaydın baskı işareti (ADR 0042 §20; MEPC.312 "değişiklik her çıktıda görünür"): iptalse
 * "VOID", revizyon > 1 ise "AMENDED – record revision n of m", onaylı değilse "NOT APPROVED".
 * Birden fazlası birlikte yazılabilir.
 */
export function recordMarks(
  t: RecordT,
  record: Pick<FormRecordDetail, 'status'>,
  printedRevisionNo: number | null = record.status.revisionNo,
): string[] {
  const marks: string[] = [];
  const latest = record.status.revisionNo;
  if (record.status.status === 'void') marks.push(t('forms.print.void'));
  if (latest !== null && latest > 1) {
    marks.push(
      t('forms.print.amended', { revision: printedRevisionNo ?? latest, revisions: latest }),
    );
  }
  if (record.status.status !== 'approved' && record.status.status !== 'void') {
    marks.push(t('forms.print.notApproved'));
  }
  return marks;
}

/** Kaydı girenin unvanı ve tam adı ("Chief Officer Deniz Yıldız"; MEPC.312(74) 6.3). */
export function enteredBy(record: FormRecordDetail): string {
  const roles = (record.enteredByRoleIds ?? [])
    .map((id) => record.roles?.[id])
    .filter((name): name is string => Boolean(name));
  const name = record.names[record.createdBy] ?? '—';
  return [roles.join(', '), name].filter(Boolean).join(' ');
}

/**
 * Kayıt dosyasının uygulama içi baskı altlığı (ADR 0042 §20; ADR 0028 #30): M8a3 altlığına kayıt
 * no ve AMENDED/VOID/NOT APPROVED satırı eklenir; giren, basım zamanı (gemide gemi saati, ofiste
 * UTC), basan, yer ve yazılım [MEPC.312(74) 6.3; NR496 4.1.2]. Kullanıcının dosyasına dokunulmaz.
 */
export function recordPrintFooter(
  t: RecordT,
  record: FormRecordDetail,
  source: PrintSource,
  options: { printedAt?: string; printedRevisionNo?: number | null } = {},
): string {
  const printedAt = options.printedAt ?? new Date().toISOString();
  const revision =
    options.printedRevisionNo === undefined ? record.status.revisionNo : options.printedRevisionNo;
  const hash = record.revisions.find((r) => r.revisionNo === revision)?.contentHash ?? null;
  return [
    t('forms.print.record', { record: record.recordNo }),
    ...recordMarks(t, record, revision),
    `${t('forms.record.formNo', { no: record.docNo })} ${revisionText(record.revisionNo)}`,
    t('forms.print.enteredBy', { name: enteredBy(record) }),
    t('forms.print.printed', {
      at: recordTime(printedAt, source.printer.timeZone),
      by: source.printer.name,
    }),
    source.printer.place,
    ...(hash ? [t('forms.print.hash', { hash: shortHash(hash) })] : []),
    source.software,
  ].join(' · ');
}

/** Kapak dosyasının adı: "FR-2026-00042 record.pdf". */
export function recordCoverFileName(record: Pick<FormRecordDetail, 'recordNo'>): string {
  return `${record.recordNo.replace(/[^A-Za-z0-9-]+/g, '-')} record.pdf`;
}

const FONT = 'Roboto';

const LINES: CustomTableLayout = {
  hLineWidth: (i) => (i === 1 ? 0.8 : 0.4),
  vLineWidth: () => 0,
  hLineColor: () => TOKENS.border,
  paddingLeft: () => 4,
  paddingRight: () => 4,
  paddingTop: () => 3,
  paddingBottom: () => 3,
  fillColor: (row) => (row === 0 ? TOKENS['table-head-bg'] : null),
};

/**
 * Kayıt kapak sayfası (ADR 0042 §20; plan M8b Task 20; M10f pdfmake kalıbı) [sorular #245]:
 * kayıt no, gemi, form ve bağlı revizyon (Rev ve içerik hash'i), doldurma yolu, kayıt günü,
 * durum; kaydı giren (unvan ve ad), basım zamanı ve basan; kayıt revizyonları (no, zaman, kişi,
 * gerekçe); onaylar (karar, kişi, rol, gemi saati ve UTC); bekleyen adım; dosyalar (ad, SHA-256
 * ilk 12 hane, boyut); işaretler ve iptal. Her sayfanın altında kayıt no, işaret, giren, basım,
 * yazılım ve sayfa x / y [MEPC.312(74) 6.3; NR496 4.1.2]. Saf işlev: gemi, ofis ve testler aynı
 * tanımı kullanır.
 */
export function recordCoverPdf(
  t: RecordT,
  record: FormRecordDetail,
  source: PrintSource,
  options: { printedAt?: string } = {},
): TDocumentDefinitions {
  const printedAt = options.printedAt ?? new Date().toISOString();
  const zone = source.printer.timeZone;
  const label = (key: string, values?: Record<string, unknown>) => t(`forms.cover.${key}`, values);
  const name = (id: string) => record.names[id] ?? '—';
  const role = (id: string) => record.roles?.[id] ?? '—';
  const marks = recordMarks(t, record);
  const fact = (key: string, value: string): TableCell[] => [
    { text: label(key), style: 'label' },
    { text: value || '—' },
  ];
  const facts: TableCell[][] = [
    fact('recordNo', record.recordNo),
    fact('vessel', record.vessel?.name ?? source.printer.place),
    fact('form', `${t('forms.record.formNo', { no: record.docNo })} - ${record.documentTitle}`),
    fact(
      'revision',
      [
        revisionText(record.revisionNo),
        record.templateContentHash
          ? t('forms.print.hash', { hash: shortHash(record.templateContentHash) })
          : null,
        t(`forms.info.sources.${record.revisionSource}`),
      ]
        .filter(Boolean)
        .join(' · '),
    ),
    fact('fillMethod', t(`forms.fillMethods.${record.fillMethod}`)),
    fact('recordDay', formatDay(record.recordDay)),
    fact('title_', record.title ?? ''),
    fact(
      'status',
      [
        t(
          `forms.status.${record.status.status === 'pending' ? 'pendingAny' : record.status.status}`,
        ),
        ...marks,
      ].join(' · '),
    ),
    fact('enteredBy', enteredBy(record)),
    fact('opened', recordTime(record.createdAt, zone)),
    fact('printed', `${recordTime(printedAt, zone)} · ${source.printer.name}`),
  ];
  const revisionRows: TableCell[][] = [
    [
      { text: label('rev'), style: 'th' },
      { text: label('at'), style: 'th' },
      { text: label('by'), style: 'th' },
      { text: label('reason'), style: 'th' },
      { text: label('hash'), style: 'th' },
    ],
    ...record.revisions.map((r): TableCell[] => [
      String(r.revisionNo),
      recordTime(r.at, zone),
      name(r.by),
      r.reason ?? (r.revisionNo === 1 ? label('firstSubmission') : '—'),
      shortHash(r.contentHash),
    ]),
  ];
  const approvalRows: TableCell[][] = [
    [
      { text: label('step'), style: 'th' },
      { text: label('decision'), style: 'th' },
      { text: label('approver'), style: 'th' },
      { text: label('shipTime'), style: 'th' },
      { text: label('utc'), style: 'th' },
    ],
    ...record.approvals.map((a): TableCell[] => [
      String(a.step),
      {
        stack: [
          t(`forms.revisions.decisions.${a.decision}`, { step: a.step }),
          ...(a.matrixVersion !== undefined || a.contentHash
            ? [
                {
                  text: [
                    a.matrixVersion !== undefined
                      ? t('forms.cover.matrix', { version: a.matrixVersion })
                      : null,
                    a.contentHash ? shortHash(a.contentHash) : null,
                  ]
                    .filter(Boolean)
                    .join(' · '),
                  style: 'muted',
                },
              ]
            : []),
          ...(a.reason ? [{ text: a.reason, style: 'muted' }] : []),
        ],
      },
      `${name(a.approverId)} · ${role(a.approverRoleId)}${a.office ? ` · ${t('forms.revisions.officeStep')}` : ''}`,
      zone ? recordTime(a.at, zone) : '—',
      recordTime(a.at, null),
    ]),
  ];
  const latest = [...record.files].sort((a, b) => a.sortOrder - b.sortOrder);
  const fileRows: TableCell[][] = [
    [
      { text: '#', style: 'th' },
      { text: label('file'), style: 'th' },
      { text: label('source'), style: 'th' },
      { text: label('sha256'), style: 'th' },
      { text: label('size'), style: 'th', alignment: 'right' },
    ],
    ...latest.map((f, index): TableCell[] => [
      String(index + 1),
      f.name,
      t(`forms.fileSources.${f.source}`),
      shortHash(f.sha256),
      { text: fileSizeText(f.size, 'en'), alignment: 'right' },
    ]),
  ];
  // İmza bloğu (ADR 0069): zincirin her adımı için rol, ad, karar, UTC ve gemi saati farkı,
  // içerik hash'inin kısası; karar verilmemiş adımın yeri boş kalmaz ("Awaiting decision")
  const signatureTime = (at: string, shipOffsetMin: number | undefined): string => {
    const offset = shipOffsetMin ?? (zone ? vesselOffsetMinutes(zone, at) : null);
    const utc = recordTime(at, null);
    return offset === null ? utc : label('signatureTime', { utc, offset: utcOffsetLabel(offset) });
  };
  // Form zincirinde (ADR 0071 §5) hazırlayanın gönderim imzası ve "hepsi" adımında rütbe başına kutu
  const signatures: Content[] = signatureRows(record).map(
    (row, index): Content => ({
      table: {
        widths: ['*'],
        dontBreakRows: true,
        body: [
          [
            {
              stack: [
                {
                  text: `${
                    row.kind
                      ? `${index + 1}. ${signatureHeading(row, t, (id) => role(id))}`
                      : label('signatureStep', {
                          step: row.step ?? index + 1,
                          role: role(row.roleIds[0] ?? ''),
                        })
                  }${row.office ? ` · ${t('forms.revisions.officeStep')}` : ''}`,
                  bold: true,
                },
                ...(row.decision
                  ? [
                      {
                        text: [
                          name(row.decision.approverId),
                          t(
                            row.prepared
                              ? 'forms.steps.submitted'
                              : `forms.status.${row.decision.decision}`,
                          ),
                          signatureTime(row.decision.at, row.decision.shipOffsetMin),
                          ...(row.decision.contentHash
                            ? [
                                label('signatureHash', {
                                  hash: shortHash(row.decision.contentHash),
                                }),
                              ]
                            : []),
                        ].join(' · '),
                      },
                      ...(row.decision.reason
                        ? [{ text: row.decision.reason, style: 'muted' }]
                        : []),
                    ]
                  : [{ text: label('unsigned'), style: 'muted' }]),
              ],
            },
          ],
        ],
      },
      layout: {
        hLineWidth: () => 0.6,
        vLineWidth: () => 0.6,
        hLineColor: () => TOKENS.border,
        vLineColor: () => TOKENS.border,
        paddingLeft: () => 6,
        paddingRight: () => 6,
        paddingTop: () => 5,
        paddingBottom: () => 5,
      },
      margin: [0, 0, 0, 6],
    }),
  );
  const table = (widths: (string | number)[], body: TableCell[][]): Content => ({
    table: { headerRows: 1, widths, body, dontBreakRows: true },
    layout: LINES,
    margin: [0, 0, 0, 12],
  });
  const content: Content[] = [
    {
      columns: [
        {
          width: '*',
          stack: [
            { text: label('title'), style: 'title' },
            { text: record.recordNo, style: 'recordNo' },
          ],
        },
        {
          width: 'auto',
          stack: [
            {
              text: record.vessel?.name ?? source.printer.place,
              style: 'muted',
              alignment: 'right',
            },
            { text: source.software, style: 'muted', alignment: 'right' },
          ],
        },
      ],
      margin: [0, 0, 0, 10],
    },
    ...marks.map(
      (mark): Content => ({
        text: mark,
        style: 'banner',
        color: mark === t('forms.print.notApproved') ? TOKENS['warning-fg'] : TOKENS['danger-fg'],
        margin: [0, 0, 0, 4],
      }),
    ),
    ...(record.pending
      ? [
          {
            text: t('forms.revisions.awaiting', {
              role: (record.pending.approverRoleIds ?? [record.pending.approverRoleId])
                .map(role)
                .join(' / '),
              since: recordTime(record.pending.since, zone),
            }),
            style: 'banner',
            color: TOKENS['warning-fg'],
            margin: [0, 0, 0, 4],
          } as Content,
          ...(record.overdue
            ? [{ text: t('forms.revisions.overdue'), color: TOKENS['danger-fg'] } as Content]
            : []),
        ]
      : []),
    {
      table: { widths: [130, '*'], body: facts },
      layout: 'noBorders',
      margin: [0, 6, 0, 12],
    },
    ...(record.flags.length > 0 || record.paperRevisionReason
      ? [
          { text: label('flags'), style: 'heading' } as Content,
          {
            ul: [
              ...record.flags.map((flag) => t(`forms.flags.${flag}`)),
              ...(record.paperRevisionReason
                ? [`${t('forms.info.paperReason')}: ${record.paperRevisionReason}`]
                : []),
            ],
            margin: [0, 0, 0, 12],
          } as Content,
        ]
      : []),
    ...(record.void
      ? [
          {
            text: t('forms.revisions.void', {
              by: name(record.void.by),
              at: recordTime(record.void.at, zone),
              reason: record.void.reason,
            }),
            style: 'banner',
            color: TOKENS['danger-fg'],
            margin: [0, 0, 0, 12],
          } as Content,
        ]
      : []),
    { text: label('revisions'), style: 'heading' },
    record.revisions.length > 0
      ? table([30, 110, 90, '*', 70], revisionRows)
      : { text: t('forms.revisions.none'), style: 'muted', margin: [0, 0, 0, 12] },
    { text: label('approvals'), style: 'heading' },
    record.approvals.length > 0
      ? table([30, 110, '*', 100, 85], approvalRows)
      : { text: label('noApprovals'), style: 'muted', margin: [0, 0, 0, 12] },
    { text: label('files'), style: 'heading' },
    latest.length > 0
      ? table([18, '*', 70, 75, 55], fileRows)
      : { text: t('forms.files.none'), style: 'muted' },
    ...(signatures.length > 0
      ? [
          { text: label('signatures'), style: 'heading', margin: [0, 12, 0, 4] } as Content,
          ...signatures,
        ]
      : []),
  ];
  const footerText = [
    t('forms.print.record', { record: record.recordNo }),
    ...marks,
    t('forms.print.enteredBy', { name: enteredBy(record) }),
    t('forms.print.printed', { at: recordTime(printedAt, zone), by: source.printer.name }),
    source.software,
  ].join(' · ');
  return {
    pageSize: 'A4',
    pageMargins: [40, 40, 40, 56],
    info: { title: `${label('title')} ${record.recordNo}`, creator: source.software },
    defaultStyle: { font: FONT, fontSize: 9, color: TOKENS['navy-900'] },
    styles: {
      title: { fontSize: 16, bold: true },
      recordNo: { fontSize: 13, bold: true, color: TOKENS['primary-600'] },
      heading: { fontSize: 10, bold: true, margin: [0, 4, 0, 4] },
      label: { color: TOKENS['text-secondary'] },
      muted: { color: TOKENS['text-secondary'] },
      th: { bold: true, color: TOKENS['text-secondary'] },
      banner: { fontSize: 11, bold: true },
    },
    content,
    footer: (page, pages) => ({
      margin: [40, 14, 40, 0],
      columns: [
        { width: '*', text: footerText, style: 'muted', fontSize: 7 },
        {
          width: 'auto',
          text: t('forms.print.page', { page, pages }),
          alignment: 'right',
          style: 'muted',
          fontSize: 7,
        },
      ],
    }),
  };
}
